import React, { useState, useEffect, useContext, createContext, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Layers,
  Phone,
  MessageCircle,
  Search,
  Play,
  Pause,
  RotateCcw,
  CheckCircle2,
  ShieldCheck,
  Check,
  Lock,
  Radio,
  Zap,
} from "lucide-react";
import { Reveal } from "./motion";
import { openConsultationModal } from "./ConsultationModal";

/* =========================================================================
   BENTO 3D CONTEXT: SHARES HOVER, TILT, AND PARALLAX STATE WITH INNER LAYERS
   ========================================================================= */

const BentoCardContext = createContext({
  isHovered: false,
  mousePos: { x: 0, y: 0 },
  tilt: { x: 0, y: 0 },
});

export const useBentoCard = () => useContext(BentoCardContext);

/* =========================================================================
   1. LAPTOP MOCKUP FOR BUILDHEALER (CINEMATIC 3D PARALLAX & DEPTH LAYERS)
   ========================================================================= */

function BuildHealerLaptopMockup() {
  const [isPlaying, setIsPlaying] = useState(true);
  const [loopProgress, setLoopProgress] = useState(0);
  const { isHovered } = useBentoCard();

  // 9-second continuous demo loop (100 ticks @ 90ms)
  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setLoopProgress((prev) => (prev + 1) % 100);
    }, 90);

    return () => clearInterval(interval);
  }, [isPlaying]);

  const step =
    loopProgress < 22
      ? 0
      : loopProgress < 44
      ? 1
      : loopProgress < 60
      ? 2
      : loopProgress < 75
      ? 3
      : loopProgress < 94
      ? 4
      : 5;

  const handleManualReset = () => {
    setLoopProgress(0);
    setIsPlaying(true);
  };

  return (
    <div
      className="w-full max-w-[480px] lg:max-w-[510px] xl:max-w-[530px] mx-auto relative select-none pt-4 pb-4"
      style={{
        perspective: 1200,
        transformStyle: "preserve-3d",
      }}
    >
      {/* 3D FLOATING LAYER 1: ESCROW VAULT SECURITY PILL (Pops out in Z-space on hover) */}
      <div
        style={{
          transform: isHovered
            ? "translateZ(90px) translateY(-14px) translateX(-4px) scale(1.06)"
            : "translateZ(0px) translateY(8px) translateX(0px) scale(0.85)",
          opacity: isHovered ? 1 : 0,
          transition: "transform 0.45s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.35s ease",
          transformStyle: "preserve-3d",
        }}
        className="absolute top-1 left-3 z-50 pointer-events-none"
      >
        <div className="bg-[#121216]/95 backdrop-blur-xl border border-emerald-500/60 rounded-xl px-3 py-1.5 shadow-[0_25px_50px_rgba(0,0,0,0.95),0_0_25px_rgba(16,185,129,0.35)] flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400">
            <Lock className="w-3.5 h-3.5" />
          </div>
          <div>
            <div className="text-[7.5px] font-mono font-bold text-white uppercase tracking-wider flex items-center gap-1.5">
              <span>Escrow Vault Active</span>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            </div>
            <div className="text-[8px] font-mono text-emerald-400 font-bold">$4,200.00 Held in Trust</div>
          </div>
        </div>
      </div>

      {/* 3D FLOATING LAYER 2: CONTRACTOR VERIFICATION BADGE (Pops forward bottom right on hover) */}
      <div
        style={{
          transform: isHovered
            ? "translateZ(105px) translateY(12px) translateX(6px) scale(1.05)"
            : "translateZ(0px) translateY(-8px) translateX(0px) scale(0.85)",
          opacity: isHovered ? 1 : 0,
          transition: "transform 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.05s, opacity 0.35s ease",
          transformStyle: "preserve-3d",
        }}
        className="absolute bottom-1 right-2 z-50 pointer-events-none"
      >
        <div className="bg-[#121216]/95 backdrop-blur-xl border border-[#0052FF]/60 rounded-xl p-2.5 shadow-[0_25px_50px_rgba(0,0,0,0.95),0_0_25px_rgba(0,82,255,0.3)] text-left min-w-[205px]">
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <div className="flex items-center gap-1.5">
              <div className="w-5 h-5 rounded-full bg-[#0052FF] text-white flex items-center justify-center font-bold text-[8px]">
                DS
              </div>
              <div>
                <div className="text-[8px] font-bold text-white leading-tight">Dave S. (Contractor)</div>
                <div className="text-[6.5px] text-neutral-400 font-mono">Milestone 2 Verified</div>
              </div>
            </div>
            <div className="px-1.5 py-0.5 rounded bg-emerald-500/20 border border-emerald-500/40 text-[7px] font-mono font-bold text-emerald-400 flex items-center gap-1">
              <Check className="w-2.5 h-2.5" />
              <span>APPROVED</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 pt-1 border-t border-white/10 text-[7px] text-neutral-300 font-mono">
            <CheckCircle2 className="w-3 h-3 text-emerald-400 flex-shrink-0" />
            <span>Disbursement Velocity: &lt; 1.2s</span>
          </div>
        </div>
      </div>

      {/* Main Laptop Body with 3D Tilt */}
      <div
        style={{
          transform: isHovered
            ? "translateZ(25px) rotateX(4deg) scale(1.02)"
            : "translateZ(0px) rotateX(0deg) scale(1)",
          transition: "transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)",
          transformStyle: "preserve-3d",
        }}
        className="relative"
      >
        {/* Laptop Screen Lid */}
        <div className="rounded-t-2xl sm:rounded-t-3xl border border-neutral-700/80 bg-[#161619] p-2 sm:p-2.5 pb-0 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.95)] relative overflow-hidden">
          {/* Top Bezel Camera Dot */}
          <div className="w-1.5 h-1.5 rounded-full bg-neutral-600 mx-auto mb-1.5" />

          {/* Screen Display Area (16:10 Ratio) */}
          <div className="rounded-t-lg sm:rounded-t-xl bg-[#FFFFFF] text-[#0B0B0B] overflow-hidden aspect-[16/10] relative flex flex-col justify-between font-sans border-t border-neutral-200">
            {/* Top Video Demo Status Bar */}
            <div className="h-7 sm:h-8 px-2.5 sm:px-3 bg-white border-b border-neutral-200 flex items-center justify-between text-[8px] sm:text-[9px]">
              {/* Logo */}
              <div className="flex items-center gap-1.5">
                <div className="w-3.5 h-3.5 rounded bg-[#0052FF] flex items-center justify-center text-white font-bold text-[7.5px]">
                  BH
                </div>
                <span className="font-bold text-[#0B0B0B] tracking-tight">BuildHealer</span>
              </div>

              {/* Video Motion Badge & Controls */}
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-neutral-100 border border-neutral-200 text-[6.5px] font-mono text-neutral-600">
                  <span className={`w-1.5 h-1.5 rounded-full ${isPlaying ? "bg-rose-500 animate-pulse" : "bg-neutral-400"}`} />
                  <span className="font-semibold">ESCROW FLOW</span>
                </div>

                <button
                  type="button"
                  onClick={() => setIsPlaying(!isPlaying)}
                  title={isPlaying ? "Pause video animation" : "Play video animation"}
                  className="w-4 h-4 rounded hover:bg-neutral-100 text-neutral-600 hover:text-neutral-900 grid place-items-center transition-colors cursor-pointer"
                >
                  {isPlaying ? <Pause className="w-2.5 h-2.5" /> : <Play className="w-2.5 h-2.5 fill-current" />}
                </button>

                <button
                  type="button"
                  onClick={handleManualReset}
                  title="Restart demo animation"
                  className="w-4 h-4 rounded hover:bg-neutral-100 text-neutral-600 hover:text-neutral-900 grid place-items-center transition-colors cursor-pointer"
                >
                  <RotateCcw className="w-2.5 h-2.5" />
                </button>
              </div>
            </div>

            {/* Hero Content Grid with Live Simulated Interactions */}
            <div className="p-2.5 sm:p-3 flex-1 grid grid-cols-12 gap-2 sm:gap-3 items-center bg-[#FAFAFC] relative overflow-hidden">
              {/* Toast Notification (Contractor Upload) */}
              <div
                className={`absolute top-2 right-2.5 z-30 transition-all duration-500 ease-out flex items-center gap-1.5 px-2 py-1 rounded bg-neutral-900/95 text-white shadow-xl border border-white/10 ${
                  step >= 1 && step < 4
                    ? "opacity-100 translate-y-0 scale-100"
                    : "opacity-0 -translate-y-2 scale-95 pointer-events-none"
                }`}
              >
                <div className="w-3.5 h-3.5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-2.5 h-2.5" />
                </div>
                <div className="text-left">
                  <div className="text-[6.5px] sm:text-[7px] font-bold leading-none text-white">Dave S. (Contractor)</div>
                  <div className="text-[5.5px] sm:text-[6px] text-neutral-300 leading-none mt-0.5">Uploaded 4 inspection photos</div>
                </div>
              </div>

              {/* Left Hero Column */}
              <div className="col-span-7 space-y-1 sm:space-y-1.5 pr-1">
                <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200/80 text-[6.5px] text-emerald-700 font-medium">
                  <ShieldCheck className="w-2.5 h-2.5 text-emerald-600" />
                  <span>Escrow Milestone Security</span>
                </div>

                <h4 className="font-display text-[11.5px] sm:text-[13.5px] font-extrabold text-[#0B0B0B] leading-tight">
                  Find Trusted Contractors for Your Home Projects.
                </h4>
                <p className="text-[7px] sm:text-[8px] text-neutral-500 leading-snug line-clamp-2">
                  Plan, track, and pay with milestone-based escrow for complete peace of mind.
                </p>

                <div className="flex items-center gap-1.5 pt-0.5">
                  <span className="px-2 py-0.5 rounded bg-[#0052FF] text-white text-[7px] sm:text-[7.5px] font-semibold shadow-xs">
                    Find Contractors
                  </span>
                  <span className="px-2 py-0.5 rounded border border-neutral-300 text-[7px] sm:text-[7.5px] font-semibold text-neutral-700 hidden sm:inline">
                    Post a Project
                  </span>
                </div>

                {/* Micro Avatars */}
                <div className="flex items-center gap-1 pt-1">
                  <div className="flex -space-x-1">
                    <span className="w-3 h-3 rounded-full bg-amber-400 border border-white text-[5px] font-bold grid place-items-center text-white">
                      JD
                    </span>
                    <span className="w-3 h-3 rounded-full bg-emerald-500 border border-white text-[5px] font-bold grid place-items-center text-white">
                      MK
                    </span>
                    <span className="w-3 h-3 rounded-full bg-sky-500 border border-white text-[5px] font-bold grid place-items-center text-white">
                      SR
                    </span>
                  </div>
                  <span className="text-[6.5px] font-medium text-neutral-600">10k+ Homeowners</span>
                </div>
              </div>

              {/* Right Escrow Milestone Card */}
              <div className="col-span-5 relative">
                <div
                  className={`rounded-lg border bg-white p-1.5 shadow-sm space-y-1 transition-all duration-300 ${
                    step === 2 || step === 3
                      ? "border-[#0052FF] ring-2 ring-[#0052FF]/20 shadow-md scale-[1.02]"
                      : "border-neutral-200"
                  }`}
                >
                  <div className="relative aspect-[16/10] rounded overflow-hidden bg-neutral-100">
                    <img
                      src="/brand/buildhealer-house.jpg"
                      alt="BuildHealer Home Renovation"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-1 left-1 px-1 py-0.2 rounded bg-black/70 backdrop-blur-xs text-[5.5px] font-mono text-white flex items-center gap-0.5">
                      <span className="w-1 h-1 rounded-full bg-amber-400" />
                      <span>ESCROW SECURED</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-[7px]">
                    <span className="font-bold text-[#0B0B0B]">Kitchen Renovation</span>
                    <span
                      className={`font-mono font-bold transition-colors duration-300 ${
                        step >= 4 ? "text-emerald-600" : "text-[#0052FF]"
                      }`}
                    >
                      {step >= 4 ? "75%" : "50%"}
                    </span>
                  </div>

                  {/* Dynamic Progress Bar */}
                  <div className="w-full h-1 bg-neutral-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#0052FF] to-emerald-500 transition-all duration-700 ease-out"
                      style={{ width: step >= 4 ? "75%" : "50%" }}
                    />
                  </div>

                  <div className="flex items-center justify-between text-[6px] text-neutral-500">
                    <span>{step >= 4 ? "Milestone 3: Finishing" : "Milestone 2: Cabinetry"}</span>
                    <span className="font-mono font-semibold text-neutral-700">$4,200 Due</span>
                  </div>

                  {/* Simulated Action Button */}
                  <div
                    className={`w-full py-1 rounded text-[6.5px] font-bold text-center transition-all duration-300 ${
                      step >= 4
                        ? "bg-emerald-500 text-white"
                        : step === 3
                        ? "bg-[#0052FF] text-white scale-95"
                        : step === 2
                        ? "bg-[#0052FF] text-white shadow-xs"
                        : "bg-[#0052FF] text-white"
                    }`}
                  >
                    {step >= 4 ? (
                      <span className="flex items-center justify-center gap-0.5">
                        <Check className="w-2 h-2" /> Milestone Approved
                      </span>
                    ) : (
                      "Review & Approve Escrow"
                    )}
                  </div>
                </div>
              </div>

              {/* Simulated Mouse Cursor */}
              <div
                className="absolute z-40 pointer-events-none transition-all duration-700 ease-out"
                style={{
                  left:
                    step === 0
                      ? "20%"
                      : step === 1
                      ? "44%"
                      : step === 2
                      ? "78%"
                      : step === 3
                      ? "78%"
                      : "86%",
                  top:
                    step === 0
                      ? "64%"
                      : step === 1
                      ? "50%"
                      : step === 2
                      ? "82%"
                      : step === 3
                      ? "82%"
                      : "38%",
                }}
              >
                <div className="relative">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" className="drop-shadow-md">
                    <path
                      d="M4 3L11 20L14 13L21 10L4 3Z"
                      fill="#0052FF"
                      stroke="#FFFFFF"
                      strokeWidth="1.5"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {step === 3 && (
                    <span className="absolute -top-1 -left-1 w-5 h-5 rounded-full bg-blue-500/40 animate-ping pointer-events-none" />
                  )}
                </div>
              </div>

              {/* Dynamic Specular Sheen Reflection that moves on hover */}
              <div
                className="absolute inset-0 pointer-events-none z-20 transition-opacity duration-300"
                style={{
                  opacity: isHovered ? 0.35 : 0.15,
                  background: isHovered
                    ? "linear-gradient(115deg, transparent 20%, rgba(255,255,255,0.4) 45%, rgba(255,255,255,0.7) 50%, rgba(255,255,255,0.4) 55%, transparent 80%)"
                    : "linear-gradient(115deg, transparent 40%, rgba(255,255,255,0.2) 50%, transparent 60%)",
                  transform: isHovered ? "translateX(20%)" : "translateX(-60%)",
                  transition: "transform 0.8s ease-out, opacity 0.3s ease",
                }}
              />
            </div>

            {/* Bottom Scrubbing Timeline Bar */}
            <div className="w-full h-[2px] bg-neutral-200 relative overflow-hidden">
              <div
                className="h-full bg-[#0052FF] transition-all duration-100 ease-linear"
                style={{ width: `${loopProgress}%` }}
              />
            </div>
          </div>
        </div>

        {/* Laptop Base / Deck */}
        <div className="relative w-[104%] -ml-[2%]">
          <div className="w-12 sm:w-16 h-1 bg-[#1a1a1d] rounded-b mx-auto relative z-10" />
          <div className="h-2.5 sm:h-3 bg-gradient-to-b from-[#38383D] via-[#242428] to-[#141416] rounded-b-xl border-t border-neutral-600/50 shadow-2xl" />
          <div className="h-3 w-4/5 mx-auto bg-black/80 blur-md rounded-full -mt-1" />
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
   2. DUAL SMARTPHONE MOCKUPS FOR PAWSUP UAE (3D SEPARATING PARALLAX DEPTH)
   ========================================================================= */

function PawsUpDualPhoneMockup() {
  const [eta, setEta] = useState(12);
  const { isHovered } = useBentoCard();

  // Live ETA countdown simulation
  useEffect(() => {
    const interval = setInterval(() => {
      setEta((prev) => (prev <= 7 ? 12 : prev - 1));
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="relative flex items-center justify-center py-6 select-none min-h-[310px]"
      style={{
        perspective: 1200,
        transformStyle: "preserve-3d",
      }}
    >
      {/* 3D FLOATING LAYER 1: LIVE GPS DISPATCH RADAR PILL (Pops out on hover) */}
      <div
        style={{
          transform: isHovered
            ? "translateZ(115px) translateY(-14px) translateX(6px) scale(1.08)"
            : "translateZ(0px) translateY(10px) translateX(0px) scale(0.85)",
          opacity: isHovered ? 1 : 0,
          transition: "transform 0.45s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.35s ease",
          transformStyle: "preserve-3d",
        }}
        className="absolute top-2 right-1 z-50 pointer-events-none"
      >
        <div className="bg-[#121216]/95 backdrop-blur-xl border border-purple-500/60 rounded-xl px-3 py-1.5 shadow-[0_25px_50px_rgba(0,0,0,0.95),0_0_25px_rgba(168,85,247,0.35)] flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-purple-500/20 border border-purple-500/40 flex items-center justify-center text-purple-400">
            <Radio className="w-3.5 h-3.5 animate-pulse text-purple-400" />
          </div>
          <div>
            <div className="text-[7.5px] font-mono font-bold text-white uppercase tracking-wider flex items-center gap-1.5">
              <span>GPS Fleet Ping</span>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" />
            </div>
            <div className="text-[8px] font-mono text-neutral-300">
              Latency: <span className="text-emerald-400 font-bold">&lt; 80ms</span> • 48 km/h
            </div>
          </div>
        </div>
      </div>

      {/* 3D FLOATING LAYER 2: DRIVER RATING & CONTACT CARD (Pops out on hover) */}
      <div
        style={{
          transform: isHovered
            ? "translateZ(105px) translateY(10px) translateX(-6px) scale(1.06)"
            : "translateZ(0px) translateY(-10px) translateX(0px) scale(0.85)",
          opacity: isHovered ? 1 : 0,
          transition: "transform 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.05s, opacity 0.35s ease",
          transformStyle: "preserve-3d",
        }}
        className="absolute bottom-1 left-1 z-50 pointer-events-none"
      >
        <div className="bg-[#121216]/95 backdrop-blur-xl border border-amber-500/50 rounded-xl p-2.5 shadow-[0_25px_50px_rgba(0,0,0,0.95),0_0_25px_rgba(245,158,11,0.3)] flex items-center gap-2.5 min-w-[195px]">
          <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-amber-600 to-amber-400 flex items-center justify-center text-white font-bold text-[8.5px] shadow-sm">
            AR
          </div>
          <div className="text-left">
            <div className="text-[8.5px] font-bold text-white flex items-center gap-1.5">
              <span>Ahmed R.</span>
              <span className="text-amber-400 font-mono text-[7px]">★ 4.9 (840 trips)</span>
            </div>
            <div className="text-[7.5px] font-mono text-[#FF5A1F] font-bold mt-0.5">
              Arriving in {eta} min • Jumeirah 1
            </div>
          </div>
        </div>
      </div>

      {/* Phone 1: Left Phone (Floats back in 3D parallax on hover) */}
      <div
        style={{
          transform: isHovered
            ? "perspective(1200px) rotateY(-18deg) rotateX(6deg) translateZ(-20px) translateX(-14px) scale(0.96)"
            : "perspective(1200px) rotateY(-6deg) rotateX(0deg) translateZ(0px) translateX(-4px) scale(1)",
          transition: "transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)",
          transformStyle: "preserve-3d",
        }}
        className="w-[140px] sm:w-[160px] lg:w-[170px] aspect-[9/18.5] rounded-[26px] sm:rounded-[30px] border-[4.5px] border-[#2C2C30] bg-[#FFFFFF] shadow-[0_20px_40px_-10px_rgba(0,0,0,0.85)] overflow-hidden relative text-[#0B0B0B] z-10"
      >
        {/* Dynamic Island */}
        <div className="pt-2 px-2.5 flex items-center justify-between text-[7px] font-mono text-neutral-400 bg-white">
          <span className="font-semibold text-neutral-800">9:41</span>
          <div className="w-10 h-2 bg-black rounded-full" />
          <span className="text-[6.5px]">5G</span>
        </div>

        {/* Content */}
        <div className="p-2.5 pt-1 space-y-1">
          <div className="flex items-center gap-1">
            <span className="text-[#6C3483] font-black text-[11px] tracking-tight">PawsUp</span>
          </div>

          <h5 className="font-display text-[9.5px] sm:text-[11px] font-extrabold text-[#0B0B0B] leading-tight">
            Trusted Pet Care Across the UAE
          </h5>
          <p className="text-[6.5px] sm:text-[7px] text-neutral-500 leading-tight">
            Grooming, vet visits, and more. Right at your doorstep.
          </p>

          {/* Puppy + Kitten Photo */}
          <div className="aspect-[4/3] rounded-lg overflow-hidden bg-neutral-50 relative flex items-center justify-center">
            <img
              src="/brand/pawsup-pets.jpg"
              alt="PawsUp Puppy and Kitten"
              className="w-full h-full object-cover object-center"
            />
          </div>

          {/* Button */}
          <div className="pt-0.5">
            <div className="w-full py-1 rounded-full bg-[#1A1A1E] text-white text-[7.5px] font-bold text-center flex items-center justify-center gap-1 shadow-xs cursor-pointer">
              <span>Book a Service</span>
              <ArrowRight className="w-2 h-2" />
            </div>
          </div>
        </div>
      </div>

      {/* Phone 2: Right Phone (Lifts forward dramatically in 3D parallax on hover) */}
      <div
        style={{
          transform: isHovered
            ? "perspective(1200px) rotateY(-2deg) rotateX(-4deg) translateZ(75px) translateX(10px) translateY(-10px) scale(1.06)"
            : "perspective(1200px) rotateY(4deg) rotateX(0deg) translateZ(20px) translateX(-18px) translateY(8px) scale(1)",
          transition: "transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)",
          transformStyle: "preserve-3d",
        }}
        className="w-[140px] sm:w-[160px] lg:w-[170px] aspect-[9/18.5] rounded-[26px] sm:rounded-[30px] border-[4.5px] border-[#2C2C30] bg-[#F7F7FA] shadow-[0_30px_60px_-10px_rgba(0,0,0,0.95)] overflow-hidden relative text-[#0B0B0B] z-20"
      >
        {/* Dynamic Island */}
        <div className="pt-2 px-2.5 flex items-center justify-between text-[7px] font-mono text-neutral-400 bg-white">
          <span className="font-semibold text-neutral-800">9:41</span>
          <div className="w-10 h-2 bg-black rounded-full" />
          <span className="text-[6.5px]">100%</span>
        </div>

        {/* Header */}
        <div className="px-2.5 py-1 bg-white border-b border-neutral-200/80 flex items-center justify-between text-[8px] font-bold">
          <span>Live Telemetry</span>
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
        </div>

        {/* Map with Animated Radar Wave Rings */}
        <div className="relative h-[105px] sm:h-[125px] bg-[#E8EAEF] overflow-hidden">
          <div className="absolute inset-0 opacity-40">
            <div className="w-full h-[1px] bg-white top-5 absolute" />
            <div className="w-full h-[2px] bg-white top-12 absolute" />
            <div className="w-full h-[1px] bg-white top-20 absolute" />
            <div className="h-full w-[2px] bg-white left-7 absolute" />
            <div className="h-full w-[2px] bg-white left-18 absolute" />
          </div>

          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
            <path
              d="M 20 85 Q 40 50 65 35"
              fill="none"
              stroke="#0052FF"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeDasharray="6 3"
              className="animate-pulse"
            />
            {/* Concentric Radar Wave Rings around Van */}
            <circle cx="65" cy="35" r="16" fill="none" stroke="#FF5A1F" strokeWidth="1" opacity="0.2" className="animate-ping" style={{ animationDuration: "2.5s" }} />
            <circle cx="65" cy="35" r="9" fill="none" stroke="#FF5A1F" strokeWidth="1.2" opacity="0.4" className="animate-ping" style={{ animationDuration: "1.8s" }} />
            <circle cx="65" cy="35" r="4.5" fill="#FF5A1F" />
            <circle cx="65" cy="35" r="2" fill="#FFFFFF" />
          </svg>

          <div className="absolute top-2 right-2 px-1.5 py-0.5 rounded bg-white shadow-xs text-[6px] font-mono font-bold text-neutral-700 flex items-center gap-1">
            <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse" />
            <span>VAN #04 • LIVE</span>
          </div>
        </div>

        {/* Overlay Card */}
        <div className="p-2 bg-white border-t border-neutral-200 space-y-1">
          <div>
            <div className="text-[6.5px] font-mono text-neutral-500">Your Pet Care Specialist</div>
            <div className="text-[8px] sm:text-[8.5px] font-bold text-[#FF5A1F] flex items-center gap-1">
              <span>Arriving in {eta} min</span>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            </div>
          </div>

          <div className="flex items-center justify-between pt-1 border-t border-neutral-100">
            <div className="flex items-center gap-1">
              <div className="w-3.5 h-3.5 rounded-full bg-amber-600 flex items-center justify-center text-[6px] text-white font-bold">
                AR
              </div>
              <div>
                <span className="text-[7.5px] font-bold block text-neutral-800 leading-tight">Ahmed R.</span>
                <span className="text-[6.5px] text-amber-500 block leading-none">★ 4.9</span>
              </div>
            </div>
            <div className="flex items-center gap-1 text-neutral-500">
              <span className="w-3.5 h-3.5 rounded-full bg-neutral-100 grid place-items-center">
                <Phone className="w-2 h-2 text-[#0052FF]" />
              </span>
              <span className="w-3.5 h-3.5 rounded-full bg-neutral-100 grid place-items-center">
                <MessageCircle className="w-2 h-2 text-emerald-600" />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
   3. PERSPECTIVE DISPLAY MOCKUP FOR INDUSDEAL (TYPEWRITER & 3D FLOATING RFQ)
   ========================================================================= */

function IndusDealPerspectiveMockup() {
  const [typedQuery, setTypedQuery] = useState("Siemens 3-Phase Motors");
  const { isHovered } = useBentoCard();

  useEffect(() => {
    const queries = [
      "Siemens 3-Phase Motors",
      "Pumps & Stainless Valves",
      "Industrial Control Valves",
      "Automation Heavy Equipment",
    ];
    let qIdx = 0;
    const interval = setInterval(() => {
      qIdx = (qIdx + 1) % queries.length;
      setTypedQuery(queries[qIdx]);
    }, 2500);

    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="w-full max-w-[480px] lg:max-w-[510px] xl:max-w-[530px] mx-auto relative select-none pt-4 pb-4"
      style={{
        perspective: 1200,
        transformStyle: "preserve-3d",
      }}
    >
      {/* 3D FLOATING LAYER 1: ELEVATED REAL-TIME SEARCH INDEX PILL (Pops out on hover) */}
      <div
        style={{
          transform: isHovered
            ? "translateZ(90px) translateY(-14px) scale(1.04)"
            : "translateZ(0px) translateY(8px) scale(0.85)",
          opacity: isHovered ? 1 : 0,
          transition: "transform 0.45s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.35s ease",
          transformStyle: "preserve-3d",
        }}
        className="absolute top-1 left-4 right-4 z-50 pointer-events-none"
      >
        <div className="bg-[#121216]/95 backdrop-blur-xl border border-[#FF5A1F]/60 rounded-xl px-3.5 py-2 shadow-[0_25px_50px_rgba(0,0,0,0.95),0_0_25px_rgba(255,90,31,0.35)] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-lg bg-[#FF5A1F]/20 border border-[#FF5A1F]/40 flex items-center justify-center text-[#FF5A1F]">
              <Search className="w-3 h-3" />
            </div>
            <div className="text-left">
              <div className="text-[7.5px] font-mono text-neutral-400 uppercase tracking-wider">
                ElasticSearch Query Stream
              </div>
              <div className="text-[9.5px] font-bold text-white font-mono flex items-center gap-1.5">
                <span>&quot;{typedQuery}&quot;</span>
                <span className="w-1.5 h-3 bg-[#FF5A1F] animate-pulse" />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="px-2 py-0.5 rounded bg-emerald-500/20 border border-emerald-500/40 text-[7px] font-mono font-bold text-emerald-400">
              &lt; 45ms LATENCY
            </div>
            <div className="px-2 py-0.5 rounded bg-white/5 border border-white/10 text-[7px] font-mono text-neutral-300 hidden sm:block">
              120k SKUs
            </div>
          </div>
        </div>
      </div>

      {/* 3D FLOATING LAYER 2: LIVE RFQ B2B QUOTE DISPATCH CARD (Pops out on hover) */}
      <div
        style={{
          transform: isHovered
            ? "translateZ(105px) translateY(12px) translateX(6px) scale(1.05)"
            : "translateZ(0px) translateY(-8px) translateX(0px) scale(0.85)",
          opacity: isHovered ? 1 : 0,
          transition: "transform 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.05s, opacity 0.35s ease",
          transformStyle: "preserve-3d",
        }}
        className="absolute bottom-1 right-2 z-50 pointer-events-none"
      >
        <div className="bg-[#121216]/95 backdrop-blur-xl border border-sky-500/60 rounded-xl p-3 shadow-[0_25px_50px_rgba(0,0,0,0.95),0_0_25px_rgba(14,165,233,0.35)] min-w-[210px] text-left">
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <span className="text-[7.5px] font-mono font-bold text-sky-400 uppercase tracking-wider flex items-center gap-1">
              <Zap className="w-2.5 h-2.5 text-sky-400" />
              <span>LIVE RFQ DISPATCH</span>
            </span>
            <span className="px-1.5 py-0.5 rounded bg-sky-500/20 border border-sky-500/40 text-[6.5px] font-mono font-bold text-sky-300">
              AUTO-ROUTED
            </span>
          </div>
          <div className="text-[9px] font-bold text-white mb-0.5">50x Siemens 3-Phase Motors</div>
          <div className="text-[7.5px] font-mono text-neutral-400 flex items-center justify-between">
            <span>Quote Est: <strong className="text-white">$34,500 USD</strong></span>
            <span className="text-emerald-400 font-semibold">12 Suppliers Notified</span>
          </div>
        </div>
      </div>

      {/* Main Display Chassis */}
      <div
        style={{
          transform: isHovered
            ? "translateZ(25px) rotateX(4deg) scale(1.02)"
            : "translateZ(0px) rotateX(0deg) scale(1)",
          transition: "transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)",
          transformStyle: "preserve-3d",
        }}
        className="relative"
      >
        {/* Display Lid */}
        <div className="rounded-t-2xl sm:rounded-t-3xl border border-neutral-700/80 bg-[#161619] p-2 sm:p-2.5 pb-0 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.95)]">
          {/* Camera Dot */}
          <div className="w-1.5 h-1.5 rounded-full bg-neutral-600 mx-auto mb-1.5" />

          {/* Screen Display */}
          <div className="rounded-t-lg sm:rounded-t-xl bg-[#F8F9FA] text-[#0B0B0B] overflow-hidden aspect-[16/10] relative flex flex-col justify-between font-sans border-t border-neutral-200">
            {/* Top Catalog Header */}
            <div className="h-7 sm:h-8 px-3 sm:px-4 bg-white border-b border-neutral-200 flex items-center justify-between text-[8.5px] sm:text-[9.5px]">
              <div className="flex items-center gap-1.5">
                <div className="w-3.5 h-3.5 rounded bg-[#FF5A1F] flex items-center justify-center text-white font-bold text-[8px]">
                  ID
                </div>
                <span className="font-bold text-[#0B0B0B] tracking-tight">IndusDeal</span>
              </div>
              <div className="flex items-center gap-3 text-neutral-500 text-[8px]">
                <span className="text-[#0B0B0B] font-semibold">Products</span>
                <span>Suppliers</span>
                <span>Industries</span>
              </div>
              <div className="px-2 py-0.5 rounded bg-[#FF5A1F] text-white font-bold text-[7.5px]">
                Get Quote
              </div>
            </div>

            {/* Typewriter Search Bar */}
            <div className="px-3 pt-2">
              <div className="w-full h-6 px-2 rounded border border-neutral-300 bg-white flex items-center gap-1.5 text-[8px] text-neutral-800 shadow-2xs">
                <Search className="w-2.5 h-2.5 text-neutral-400" />
                <span className="font-mono text-neutral-900 font-medium">
                  {typedQuery}
                </span>
                <span className="w-1.5 h-3 bg-[#FF5A1F] animate-pulse" />
              </div>
            </div>

            {/* Catalog Grid */}
            <div className="p-3 pt-1 flex-1 flex flex-col justify-end">
              <div className="text-[7.5px] font-bold text-neutral-700 uppercase tracking-wider mb-1">
                Industrial Automation
              </div>
              <div className="grid grid-cols-4 gap-2">
                <div className="col-span-1 rounded border border-neutral-200 bg-white p-1.5 text-[7px] space-y-1 shadow-2xs">
                  <div className="font-bold text-neutral-800">Electrical & Power</div>
                  <div className="text-neutral-500 text-[6px]">Mechanical Components</div>
                  <div className="text-neutral-500 text-[6px]">Pumps & Valves</div>
                  <div className="text-neutral-500 text-[6px]">Tools & Hardware</div>
                </div>

                <div className="col-span-1 rounded border border-neutral-200 bg-white p-1 flex flex-col items-center justify-between shadow-2xs">
                  <div className="aspect-square w-full rounded bg-neutral-100 flex items-center justify-center overflow-hidden">
                    <img
                      src="/brand/indusdeal-motor.jpg"
                      alt="Industrial Motor"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="text-[6.5px] font-bold text-neutral-800 mt-0.5">Industrial Motors</span>
                </div>

                <div className="col-span-1 rounded border border-neutral-200 bg-white p-1 flex flex-col items-center justify-between shadow-2xs">
                  <div className="aspect-square w-full rounded bg-neutral-100 flex items-center justify-center overflow-hidden">
                    <img
                      src="/brand/indusdeal-valve.jpg"
                      alt="Industrial Pump"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="text-[6.5px] font-bold text-neutral-800 mt-0.5">Pumps & Valves</span>
                </div>

                <div className="col-span-1 rounded border border-neutral-200 bg-white p-1 flex flex-col items-center justify-between shadow-2xs">
                  <div className="aspect-square w-full rounded bg-neutral-100 flex items-center justify-center overflow-hidden">
                    <img
                      src="/brand/indusdeal-control.jpg"
                      alt="Control Equipment"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="text-[6.5px] font-bold text-neutral-800 mt-0.5">Control Equipment</span>
                </div>
              </div>
            </div>

            {/* Specular Sheen Reflection */}
            <div
              className="absolute inset-0 pointer-events-none z-20 transition-opacity duration-300"
              style={{
                opacity: isHovered ? 0.35 : 0.15,
                background: isHovered
                  ? "linear-gradient(115deg, transparent 20%, rgba(255,255,255,0.4) 45%, rgba(255,255,255,0.7) 50%, rgba(255,255,255,0.4) 55%, transparent 80%)"
                  : "linear-gradient(115deg, transparent 40%, rgba(255,255,255,0.2) 50%, transparent 60%)",
                transform: isHovered ? "translateX(20%)" : "translateX(-60%)",
                transition: "transform 0.8s ease-out, opacity 0.3s ease",
              }}
            />
          </div>
        </div>

        {/* Display Base / Stand */}
        <div className="relative w-[104%] -ml-[2%]">
          <div className="w-12 sm:w-16 h-1 bg-[#1a1a1d] rounded-b mx-auto relative z-10" />
          <div className="h-2.5 sm:h-3 bg-gradient-to-b from-[#38383D] via-[#242428] to-[#141416] rounded-b-xl border-t border-neutral-600/50 shadow-2xl" />
          <div className="h-3 w-4/5 mx-auto bg-black/80 blur-md rounded-full -mt-1" />
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
   INTERACTIVE 3D BENTO CARD WRAPPER
   Dynamic 3D perspective tilt & interactive cursor spotlight glow
   ========================================================================= */

function InteractiveBentoCard({ children, className = "", glowColor = "rgba(255, 90, 31, 0.15)" }) {
  const cardRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setMousePos({ x, y });

    // Dynamic 3D tilt (-7 to +7 degrees)
    const rotateY = ((x / rect.width) - 0.5) * 8;
    const rotateX = ((y / rect.height) - 0.5) * -8;
    setTilt({ x: rotateX, y: rotateY });
  };

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => {
    setIsHovered(false);
    setTilt({ x: 0, y: 0 });
  };

  return (
    <BentoCardContext.Provider value={{ isHovered, mousePos, tilt }}>
      <div
        ref={cardRef}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          transform: isHovered
            ? `perspective(1200px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) translateY(-4px)`
            : "perspective(1200px) rotateX(0deg) rotateY(0deg) translateY(0px)",
          transformStyle: "preserve-3d",
          transition: isHovered ? "transform 0.12s ease-out" : "transform 0.55s cubic-bezier(0.22, 1, 0.36, 1)",
        }}
        className={`group rounded-2xl border border-white/10 bg-[#111115]/90 backdrop-blur-md p-5 sm:p-6 lg:p-7 shadow-2xl relative transition-colors duration-300 hover:border-white/25 overflow-visible ${className}`}
      >
        {/* Dynamic Cursor Spotlight Glow */}
        <div
          className="pointer-events-none absolute inset-0 rounded-2xl transition-opacity duration-300 overflow-hidden"
          style={{
            opacity: isHovered ? 1 : 0,
            background: `radial-gradient(460px circle at ${mousePos.x}px ${mousePos.y}px, ${glowColor}, transparent 70%)`,
          }}
        />

        {/* Ambient Subtle Edge Shimmer */}
        <div
          className="pointer-events-none absolute inset-0 rounded-2xl border border-white/0 group-hover:border-white/20 transition-all duration-500"
          style={{
            boxShadow: isHovered ? `inset 0 0 24px ${glowColor}` : "none",
          }}
        />

        <div style={{ transformStyle: "preserve-3d" }}>
          {children}
        </div>
      </div>
    </BentoCardContext.Provider>
  );
}

/* =========================================================================
   MAIN COMPONENT: INTERACTIVE 3D BENTO COMMAND CENTER
   All 3 project records visible simultaneously with 3D Parallax Depth Layers
   ========================================================================= */

export default function ProvenImplementations({ onOpenConsultation }) {
  const handleConsultation = () => {
    if (onOpenConsultation) {
      onOpenConsultation("Proven Implementations CTA");
    } else {
      openConsultationModal("Proven Implementations CTA");
    }
  };

  return (
    <section
      id="proven-implementations"
      className="py-14 sm:py-16 lg:py-20 bg-[#0B0B0B] text-white relative section-dark overflow-hidden"
    >
      {/* Background Architectural Contour Lines */}
      <div className="absolute inset-0 pointer-events-none">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M -100,550 Q 200,480 600,650"
            fill="none"
            stroke="#FF5A1F"
            strokeWidth="1.2"
            opacity="0.25"
          />
          <circle cx="20%" cy="50%" r="480" fill="none" stroke="#FF5A1F" strokeWidth="0.6" opacity="0.18" strokeDasharray="3 3" />
          <circle cx="85%" cy="85%" r="320" fill="none" stroke="#FFFFFF" strokeWidth="0.4" opacity="0.1" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10">
        {/* ===============================================================
            HEADER INTRODUCTION & TELEMETRY COUNTER
            =============================================================== */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 mb-8 sm:mb-10">
          <div className="max-w-2xl">
            <Reveal>
              <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-neutral-400 mb-3">
                <span className="w-2 h-2 rounded-full bg-[#FF5A1F]" />
                <span>PROVEN IMPLEMENTATIONS</span>
              </div>
            </Reveal>

            <Reveal delay={0.05}>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-[44px] font-bold tracking-tight text-white leading-[1.1]">
                Real Products. Real <span className="text-[#FF5A1F]">Impact.</span>
              </h2>
            </Reveal>

            <Reveal delay={0.1}>
              <p className="mt-3 text-xs sm:text-sm text-neutral-400 font-sans leading-relaxed">
                From complex escrow marketplaces to real-time mobile platforms and high-scale industrial catalogs - all production-tested and live.
              </p>
            </Reveal>
          </div>

          {/* Right Status Badge */}
          <Reveal delay={0.12}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#16161B] border border-white/10 text-xs font-mono text-neutral-300 self-start md:self-auto shadow-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="font-semibold text-white">3 LIVE PRODUCTION RECORDS</span>
              <span className="text-neutral-500">•</span>
              <span className="text-[11px] text-neutral-400 font-sans">Hover to inspect 3D layers</span>
            </div>
          </Reveal>
        </div>

        {/* ===============================================================
            INTERACTIVE 3D BENTO COMMAND CENTER GRID
            =============================================================== */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-6">
          {/* -------------------------------------------------------------
              RECORD 01: BUILDHEALER (7 Cols)
              ------------------------------------------------------------- */}
          <div className="lg:col-span-7 flex">
            <InteractiveBentoCard
              glowColor="rgba(0, 82, 255, 0.2)"
              className="w-full flex flex-col justify-between"
            >
              <div>
                {/* Header Row */}
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold text-[#FF5A1F]">01</span>
                    <div className="w-4 h-[1px] bg-neutral-700" />
                    <span className="text-[10px] font-mono uppercase tracking-widest text-neutral-400 font-semibold">
                      MARKETPLACE ESCROW PLATFORM
                    </span>
                  </div>

                  <Link
                    to="/portfolio/buildhealer"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-neutral-300 hover:text-[#FF5A1F] transition-colors group/link"
                  >
                    <span>View Case Study</span>
                    <ArrowUpRight className="w-3.5 h-3.5 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5 transition-transform" />
                  </Link>
                </div>

                <h3 className="font-display text-xl sm:text-2xl font-bold text-white tracking-tight mb-1.5">
                  BuildHealer
                </h3>
                <p className="text-xs text-neutral-300 leading-relaxed mb-4">
                  Escrow milestone tracking and contractor marketplace built for complete accountability and financial trust.
                </p>

                {/* Live Video Animated Laptop Mockup with 3D Parallax Layers */}
                <div className="py-2 overflow-visible">
                  <BuildHealerLaptopMockup />
                </div>
              </div>

              {/* Engineering Records Strip */}
              <div className="mt-5 pt-4 border-t border-white/10">
                <div className="grid grid-cols-3 gap-2 bg-black/40 rounded-xl p-2.5 sm:p-3 border border-white/5 text-center">
                  <div>
                    <div className="text-sm sm:text-base font-bold font-mono text-white">$4.2M+</div>
                    <div className="text-[9px] text-neutral-400 font-mono mt-0.5">Escrow Secured</div>
                  </div>
                  <div className="border-x border-white/10">
                    <div className="text-sm sm:text-base font-bold font-mono text-emerald-400">100%</div>
                    <div className="text-[9px] text-neutral-400 font-mono mt-0.5">Milestone Safety</div>
                  </div>
                  <div>
                    <div className="text-sm sm:text-base font-bold font-mono text-blue-400">&lt; 1.2s</div>
                    <div className="text-[9px] text-neutral-400 font-mono mt-0.5">Disbursement Velocity</div>
                  </div>
                </div>
              </div>
            </InteractiveBentoCard>
          </div>

          {/* -------------------------------------------------------------
              RECORD 02: PAWSUP UAE (5 Cols)
              ------------------------------------------------------------- */}
          <div className="lg:col-span-5 flex">
            <InteractiveBentoCard
              glowColor="rgba(108, 52, 131, 0.28)"
              className="w-full flex flex-col justify-between"
            >
              <div>
                {/* Header Row */}
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold text-[#FF5A1F]">02</span>
                    <div className="w-4 h-[1px] bg-neutral-700" />
                    <span className="text-[10px] font-mono uppercase tracking-widest text-neutral-400 font-semibold">
                      MOBILE FLEET PLATFORM
                    </span>
                  </div>

                  <Link
                    to="/portfolio/pawsup"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-neutral-300 hover:text-[#FF5A1F] transition-colors group/link"
                  >
                    <span>View Case Study</span>
                    <ArrowUpRight className="w-3.5 h-3.5 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5 transition-transform" />
                  </Link>
                </div>

                <h3 className="font-display text-xl sm:text-2xl font-bold text-white tracking-tight mb-1.5">
                  PawsUp UAE
                </h3>
                <p className="text-xs text-neutral-300 leading-relaxed mb-4">
                  Cross-platform mobile apps with live van GPS telemetry, driver dispatch, and multi-currency billing.
                </p>

                {/* Dual Smartphones Mockup with 3D Separating Parallax Depth */}
                <div className="py-2 overflow-visible">
                  <PawsUpDualPhoneMockup />
                </div>
              </div>

              {/* Engineering Records Strip */}
              <div className="mt-5 pt-4 border-t border-white/10">
                <div className="grid grid-cols-3 gap-2 bg-black/40 rounded-xl p-2.5 sm:p-3 border border-white/5 text-center">
                  <div>
                    <div className="text-sm sm:text-base font-bold font-mono text-white">15k+</div>
                    <div className="text-[9px] text-neutral-400 font-mono mt-0.5">Monthly Trips</div>
                  </div>
                  <div className="border-x border-white/10">
                    <div className="text-sm sm:text-base font-bold font-mono text-emerald-400">&lt; 80ms</div>
                    <div className="text-[9px] text-neutral-400 font-mono mt-0.5">GPS Latency</div>
                  </div>
                  <div>
                    <div className="text-sm sm:text-base font-bold font-mono text-amber-400">4.9 ★</div>
                    <div className="text-[9px] text-neutral-400 font-mono mt-0.5">App Store Rating</div>
                  </div>
                </div>
              </div>
            </InteractiveBentoCard>
          </div>

          {/* -------------------------------------------------------------
              RECORD 03: INDUSDEAL (12 Cols Panoramic Bento Card)
              ------------------------------------------------------------- */}
          <div className="lg:col-span-12">
            <InteractiveBentoCard
              glowColor="rgba(255, 90, 31, 0.22)"
              className="w-full"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-center">
                {/* Left Information & Records Column (5 cols) */}
                <div className="lg:col-span-5 space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold text-[#FF5A1F]">03</span>
                    <div className="w-4 h-[1px] bg-neutral-700" />
                    <span className="text-[10px] font-mono uppercase tracking-widest text-neutral-400 font-semibold">
                      B2B INDUSTRIAL CATALOG ENGINE
                    </span>
                  </div>

                  <h3 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight">
                    IndusDeal
                  </h3>

                  <p className="text-xs sm:text-[13px] text-neutral-300 leading-relaxed">
                    High-concurrency industrial catalog search engine with structured product taxonomy, multi-tier specifications, and sub-second Elasticsearch indexing.
                  </p>

                  {/* Architecture Badges */}
                  <div className="space-y-2 pt-1">
                    <div className="flex items-start gap-2.5 text-xs text-neutral-300">
                      <div className="w-5 h-5 rounded-full bg-[#FF5A1F]/15 border border-[#FF5A1F]/40 flex items-center justify-center text-[#FF5A1F] flex-shrink-0 mt-0.5">
                        <Search className="w-2.5 h-2.5" />
                      </div>
                      <span>
                        <strong className="text-white">Elastic Search Architecture:</strong> Real-time indexing across 120,000+ complex industrial SKUs and technical attributes.
                      </span>
                    </div>

                    <div className="flex items-start gap-2.5 text-xs text-neutral-300">
                      <div className="w-5 h-5 rounded-full bg-[#FF5A1F]/15 border border-[#FF5A1F]/40 flex items-center justify-center text-[#FF5A1F] flex-shrink-0 mt-0.5">
                        <Layers className="w-2.5 h-2.5" />
                      </div>
                      <span>
                        <strong className="text-white">High-Load Concurrency:</strong> Cached query aggregation handling thousands of concurrent industrial RFQ quote dispatches.
                      </span>
                    </div>
                  </div>

                  {/* Telemetry Strip */}
                  <div className="grid grid-cols-3 gap-2 bg-black/40 rounded-xl p-2.5 sm:p-3 border border-white/5 text-center pt-2">
                    <div>
                      <div className="text-sm sm:text-base font-bold font-mono text-white">120k+</div>
                      <div className="text-[9px] text-neutral-400 font-mono mt-0.5">SKUs Indexed</div>
                    </div>
                    <div className="border-x border-white/10">
                      <div className="text-sm sm:text-base font-bold font-mono text-emerald-400">&lt; 45ms</div>
                      <div className="text-[9px] text-neutral-400 font-mono mt-0.5">Query Latency</div>
                    </div>
                    <div>
                      <div className="text-sm sm:text-base font-bold font-mono text-amber-400">99.98%</div>
                      <div className="text-[9px] text-neutral-400 font-mono mt-0.5">Uptime SLA</div>
                    </div>
                  </div>

                  <div>
                    <Link
                      to="/portfolio/indusdeal"
                      className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-white hover:text-[#FF5A1F] transition-colors relative group/link pb-0.5"
                    >
                      <span>View Full Case Study</span>
                      <ArrowUpRight className="w-4 h-4 text-white group-hover/link:text-[#FF5A1F] group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5 transition-transform" />
                      <div className="absolute bottom-0 left-0 right-0 h-[1.5px] bg-[#FF5A1F]" />
                    </Link>
                  </div>
                </div>

                {/* Right Mockup Column with Live Typewriter Search & 3D Floating Layers (7 cols) */}
                <div className="lg:col-span-7 flex justify-center items-center py-2 overflow-visible">
                  <IndusDealPerspectiveMockup />
                </div>
              </div>
            </InteractiveBentoCard>
          </div>
        </div>

        {/* ===============================================================
            COMPACT BOTTOM CTA
            =============================================================== */}
        <div className="mt-10 pt-8 border-t border-white/10">
          <Reveal>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5">
              <div>
                <span className="text-[10px] font-mono uppercase tracking-widest text-neutral-400 font-semibold block mb-1">
                  READY TO BUILD WHAT'S NEXT?
                </span>
                <h3 className="font-display text-xl sm:text-2xl font-bold text-white tracking-tight">
                  Have a complex product to build?
                </h3>
              </div>

              <div className="flex items-center gap-4 flex-shrink-0">
                <p className="text-xs text-neutral-400 font-sans max-w-xs leading-relaxed hidden md:block">
                  Let's discuss your requirements and explore the right technical approach.
                </p>

                <button
                  type="button"
                  onClick={handleConsultation}
                  className="inline-flex items-center justify-center gap-2 px-6 py-2.5 sm:py-3 rounded-full bg-[#FF5A1F] text-white text-xs sm:text-sm font-semibold hover:bg-[#ff7038] hover:shadow-lg hover:shadow-[#FF5A1F]/25 transition-all cursor-pointer group flex-shrink-0"
                >
                  <span>Discuss Your Project</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
